import React, { useState, useEffect, useCallback } from "react";
import { db } from "./firebase";
import { collection, getDocs, doc, updateDoc, deleteField } from "firebase/firestore";

// ── Change this to whatever you want your admin password to be ────────────────
const ADMIN_PASSWORD = "mathsmaster2024";

const flatLevelCount = 29; // total levels in curriculum

const PX = "'Press Start 2P', monospace";

// ── Design system ─────────────────────────────────────────────────────────────
const C = {
  bg:"#0d0a1a", bgCard:"#1a1035", bgFlat:"#130d26", bgAlt:"#0f0b1e",
  border:"#3b2878", borderHi:"#7c3aed", shadow:"#06030f",
  text:"#e2d4ff", textSub:"#9b80d4", textDim:"#4a3668",
  gold:"#fbbf24", goldDim:"#92670f",
  green:"#34d399",
  red:"#f87171",
  purple:"#c4b5fd", purpleMid:"#7c3aed",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function masteredCount(levelProgress) {
  if (!levelProgress) return 0;
  return Object.values(levelProgress).filter(p => p.state === "mastered").length;
}

function lastUsedLabel(profile) {
  const ts = profile.history?.[0]?.id ? parseInt(profile.history[0].id, 10) : null;
  const date = ts ? new Date(ts) : (profile.lastCompletedDate ? new Date(profile.lastCompletedDate) : null);
  if (!date || isNaN(date.getTime())) return "Never";

  const now = new Date();
  const dayDiff = Math.floor((now - date) / 86400000);
  const dateStr = date.toLocaleDateString("en-GB", { day:"numeric", month:"short" });
  const timeStr = ts ? date.toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" }) : null;

  let relative;
  if (dayDiff === 0) relative = "Today";
  else if (dayDiff === 1) relative = "Yesterday";
  else relative = `${dayDiff}d ago`;

  return timeStr ? `${relative} · ${dateStr} at ${timeStr}` : `${relative} · ${dateStr}`;
}

function maskPin(pin) {
  if (!pin || pin.length < 2) return "••••";
  return pin[0] + "•".repeat(pin.length - 1);
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function Pill({ label, value, color = C.textSub }) {
  return (
    <span style={{ display:"inline-flex", flexDirection:"column", alignItems:"center",
      background:"rgba(0,0,0,0.35)", border:`2px solid ${color}44`, padding:"4px 10px",
      marginRight:6, marginBottom:4, minWidth:60 }}>
      <span style={{ fontSize:13, fontWeight:900, color }}>{value}</span>
      <span style={{ fontSize:9, color:C.textSub, fontWeight:700, marginTop:1 }}>{label}</span>
    </span>
  );
}

// ── Account detail modal ──────────────────────────────────────────────────────
function AccountModal({ profile, onClose, onDelete, onResetProgress }) {
  const mastered = masteredCount(profile.levelProgress);
  const bossKillCount = Object.keys(profile.bossKills || {}).length;
  const [confirming, setConfirming] = useState(null); // "delete" | "reset"

  return (
    <div style={{ position:"fixed", inset:0, zIndex:9999, background:"rgba(0,0,0,0.8)",
      display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background:C.bg, border:`4px solid ${C.purpleMid}`, boxShadow:`8px 8px 0 ${C.shadow}`,
        padding:28, maxWidth:500, width:"100%", fontFamily:"'Nunito',sans-serif" }}>
        <div style={{ fontFamily:PX, fontSize:11, color:C.gold, marginBottom:4 }}>{profile.name}</div>
        <div style={{ fontSize:11, color:C.textSub, marginBottom:16 }}>PIN: {maskPin(profile.id)} · Last active: {lastUsedLabel(profile)}</div>

        <div style={{ display:"flex", flexWrap:"wrap", marginBottom:16 }}>
          <Pill label="Questions" value={profile.totalQuestions ?? 0} color={C.green} />
          <Pill label="Mastered"  value={`${mastered}/${flatLevelCount}`} color={C.gold} />
          <Pill label="Streak"    value={`${profile.streak ?? 0}d`} color={C.gold} />
          <Pill label="Best Streak" value={`${profile.bestStreak ?? 0}d`} color={C.red} />
          <Pill label="Badges"    value={profile.badges?.length ?? 0} color={C.purple} />
          <Pill label="Boss Kills" value={bossKillCount} color={C.red} />
        </div>

        {/* Level progress breakdown */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:800, color:C.textSub, marginBottom:8 }}>LEVEL PROGRESS</div>
          <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
            {Object.entries(profile.levelProgress || {}).map(([id, p]) => (
              <span key={id} style={{ fontSize:10, fontWeight:700, padding:"2px 7px",
                background: p.state==="mastered" ? `${C.gold}33` : p.state==="speed" ? `${C.gold}22` : "rgba(255,255,255,0.05)",
                border:`1px solid ${p.state==="mastered" ? C.gold : p.state==="speed" ? C.goldDim : C.bgFlat}`,
                color: p.state==="mastered" ? C.gold : p.state==="speed" ? C.goldDim : C.textSub }}>
                {id}
              </span>
            ))}
            {Object.keys(profile.levelProgress || {}).length === 0 && <span style={{ fontSize:11, color:C.textDim }}>No progress yet</span>}
          </div>
        </div>

        {/* Badges */}
        {profile.badges?.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.textSub, marginBottom:6 }}>BADGES EARNED</div>
            <div style={{ fontSize:11, color:C.text, lineHeight:1.8 }}>{profile.badges.join(", ")}</div>
          </div>
        )}

        {/* Boss kills */}
        {bossKillCount > 0 && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:800, color:C.textSub, marginBottom:6 }}>BOSSES DEFEATED</div>
            <div style={{ fontSize:11, color:C.text }}>{Object.keys(profile.bossKills).join(", ")}</div>
          </div>
        )}

        {/* Raw data debug */}
        <details style={{ marginBottom:12 }}>
          <summary style={{ fontSize:10, color:C.textDim, cursor:"pointer", fontWeight:700 }}>RAW DATA (debug)</summary>
          <pre style={{ fontSize:9, color:C.textSub, background:C.shadow, padding:10, overflow:"auto", maxHeight:160, marginTop:6 }}>
            {JSON.stringify(profile, null, 2)}
          </pre>
        </details>

        {/* Actions */}
        {confirming ? (
          <div style={{ background:`${C.red}1e`, border:`2px solid ${C.red}`, padding:14, marginTop:8 }}>
            <div style={{ fontSize:12, fontWeight:800, color:C.red, marginBottom:10 }}>
              {confirming === "delete" ? `Delete ${profile.name}'s account permanently?` : `Reset ${profile.name}'s progress? (keeps account)`}
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => { confirming === "delete" ? onDelete() : onResetProgress(); setConfirming(null); }}
                style={{ fontFamily:PX, fontSize:8, lineHeight:1.8, padding:"8px 16px", background:C.red, border:`3px solid ${C.shadow}`, cursor:"pointer", color:C.bg }}>
                CONFIRM
              </button>
              <button onClick={() => setConfirming(null)}
                style={{ fontFamily:PX, fontSize:8, lineHeight:1.8, padding:"8px 16px", background:C.bgFlat, border:`3px solid ${C.textDim}`, cursor:"pointer", color:C.textSub }}>
                CANCEL
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", gap:10, marginTop:8, flexWrap:"wrap" }}>
            <button onClick={() => setConfirming("reset")}
              style={{ fontFamily:PX, fontSize:8, lineHeight:1.8, padding:"8px 14px", background:`${C.gold}26`, border:`3px solid ${C.gold}`, cursor:"pointer", color:C.gold }}>
              RESET PROGRESS
            </button>
            <button onClick={() => setConfirming("delete")}
              style={{ fontFamily:PX, fontSize:8, lineHeight:1.8, padding:"8px 14px", background:`${C.red}26`, border:`3px solid ${C.red}`, cursor:"pointer", color:C.red }}>
              DELETE ACCOUNT
            </button>
            <button onClick={onClose}
              style={{ fontFamily:PX, fontSize:8, lineHeight:1.8, padding:"8px 14px", background:C.bgFlat, border:`3px solid ${C.textDim}`, cursor:"pointer", color:C.textSub, marginLeft:"auto" }}>
              CLOSE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main admin panel ──────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [authed, setAuthed]         = useState(false);
  const [pwInput, setPwInput]       = useState("");
  const [pwError, setPwError]       = useState(false);
  const [profiles, setProfiles]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [selected, setSelected]     = useState(null);
  const [search, setSearch]         = useState("");
  const [sortBy, setSortBy]         = useState("lastActive");

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "profiles"));
      const data = [];
      snap.docs.forEach(d => {
        const docData = d.data();
        const nestedProfiles = docData.profiles;
        if (nestedProfiles && typeof nestedProfiles === "object") {
          Object.entries(nestedProfiles).forEach(([profileId, profile]) => {
            data.push({ ...profile, id: profileId, _docId: d.id });
          });
        } else {
          data.push({ id: d.id, _docId: d.id, ...docData });
        }
      });
      setProfiles(data);
    } catch (e) {
      console.error("Failed to fetch profiles:", e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { if (authed) fetchProfiles(); }, [authed, fetchProfiles]);

  const handleLogin = () => {
    if (pwInput === ADMIN_PASSWORD) { setAuthed(true); setPwError(false); }
    else { setPwError(true); setPwInput(""); }
  };

  const handleDelete = async (profile) => {
    const docRef = doc(db, "profiles", profile._docId);
    await updateDoc(docRef, { [`profiles.${profile.id}`]: deleteField() });
    setSelected(null);
    await fetchProfiles();
  };

  const handleResetProgress = async (profile) => {
    const { _docId, id, name } = profile;
    const reset = { id, name, levelProgress: {}, badges: [], totalQuestions: 0,
      streak: 0, bestStreak: 0, lastCompletedDate: "", history: [],
      consecutivePerfects: 0, bossKills: {}, placementDone: false };
    await updateDoc(doc(db, "profiles", _docId), { [`profiles.${id}`]: reset });
    setSelected(null);
    await fetchProfiles();
  };

  // ── Login screen ─────────────────────────────────────────────────────────
  if (!authed) return (
    <div style={{ minHeight:"100vh", background:C.bg, display:"flex", alignItems:"center",
      justifyContent:"center", fontFamily:"'Nunito',sans-serif" }}>
      <div style={{ background:C.bg, border:`4px solid ${C.purpleMid}`, boxShadow:`8px 8px 0 ${C.shadow}`,
        padding:40, maxWidth:380, width:"100%", textAlign:"center" }}>
        <div style={{ fontFamily:PX, fontSize:12, color:C.gold, lineHeight:1.8, marginBottom:8 }}>Admin Panel</div>
        <div style={{ fontSize:13, color:C.textSub, fontWeight:700, marginBottom:24 }}>Maths App — Owner Access</div>
        <input
          type="password"
          placeholder="Admin password"
          value={pwInput}
          onChange={e => { setPwInput(e.target.value); setPwError(false); }}
          onKeyDown={e => e.key === "Enter" && handleLogin()}
          style={{ width:"100%", padding:"12px 16px", fontSize:14, fontWeight:700,
            background:C.bgFlat, border:`3px solid ${pwError ? C.red : C.purpleMid}`,
            color:C.text, outline:"none", boxSizing:"border-box", marginBottom:8 }}
          autoFocus
        />
        {pwError && <div style={{ fontSize:12, color:C.red, fontWeight:700, marginBottom:8 }}>Incorrect password</div>}
        <button onClick={handleLogin}
          style={{ width:"100%", fontFamily:PX, fontSize:9, lineHeight:1.8, padding:"12px",
            background:C.purpleMid, border:`3px solid ${C.shadow}`, boxShadow:`4px 4px 0 ${C.shadow}`,
            cursor:"pointer", color:C.text, marginTop:4 }}>
          LOGIN
        </button>
      </div>
    </div>
  );

  // ── Sort & filter ─────────────────────────────────────────────────────────
  const filtered = profiles
    .filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "lastActive") return new Date(b.lastCompletedDate || 0) - new Date(a.lastCompletedDate || 0);
      if (sortBy === "questions")  return (b.totalQuestions ?? 0) - (a.totalQuestions ?? 0);
      if (sortBy === "mastered")   return masteredCount(b.levelProgress) - masteredCount(a.levelProgress);
      if (sortBy === "name")       return (a.name ?? "").localeCompare(b.name ?? "");
      return 0;
    });

  const totalQ    = profiles.reduce((s, p) => s + (p.totalQuestions ?? 0), 0);
  const avgStreak = profiles.length ? Math.round(profiles.reduce((s, p) => s + (p.streak ?? 0), 0) / profiles.length) : 0;
  const activeToday = profiles.filter(p => p.lastCompletedDate === new Date().toISOString().slice(0,10)).length;

  // ── Dashboard ─────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight:"100vh", background:C.bg, fontFamily:"'Nunito',sans-serif", color:C.text }}>
      {/* Header */}
      <div style={{ background:C.bg, borderBottom:`3px solid ${C.purpleMid}`, padding:"16px 24px",
        display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontFamily:PX, fontSize:11, color:C.gold, lineHeight:1.8 }}>Admin Panel</div>
          <div style={{ fontSize:12, color:C.textSub, fontWeight:700 }}>Maths App — Owner Dashboard</div>
        </div>
        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <button onClick={fetchProfiles}
            style={{ fontFamily:PX, fontSize:7, lineHeight:1.8, padding:"6px 14px",
              background:`${C.purpleMid}33`, border:`2px solid ${C.purpleMid}`, cursor:"pointer", color:C.purple }}>
            ↻ REFRESH
          </button>
          <button onClick={() => { setAuthed(false); setPwInput(""); }}
            style={{ fontFamily:PX, fontSize:7, lineHeight:1.8, padding:"6px 14px",
              background:`${C.red}1a`, border:`2px solid ${C.red}`, cursor:"pointer", color:C.red }}>
            LOGOUT
          </button>
        </div>
      </div>

      <div style={{ padding:"20px 24px", maxWidth:1100, margin:"0 auto" }}>
        {/* Summary stats */}
        <div style={{ display:"flex", gap:12, flexWrap:"wrap", marginBottom:20 }}>
          {[
            { label:"Total Accounts",  value:profiles.length,             color:C.purpleMid },
            { label:"Active Today",    value:activeToday,                 color:C.green },
            { label:"Total Questions", value:totalQ.toLocaleString(),     color:C.gold },
            { label:"Avg Streak",      value:`${avgStreak}d`,             color:C.purple },
          ].map(s => (
            <div key={s.label} style={{ flex:"1 1 160px", background:C.bg,
              border:`3px solid ${s.color}44`, boxShadow:`4px 4px 0 ${s.color}22`, padding:"14px 18px" }}>
              <div style={{ fontSize:26, fontWeight:900, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:11, fontWeight:700, color:C.textSub, marginTop:2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search & sort */}
        <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
          <input
            type="text"
            placeholder="Search by name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex:"1 1 200px", padding:"8px 14px", fontSize:13, fontWeight:700,
              background:C.bg, border:`2px solid ${C.bgFlat}`, color:C.text, outline:"none" }}
          />
          <div style={{ display:"flex", gap:6 }}>
            {[["lastActive","Last Active"],["questions","Questions"],["mastered","Mastered"],["name","Name"]].map(([key,label]) => (
              <button key={key} onClick={() => setSortBy(key)}
                style={{ fontFamily:PX, fontSize:7, lineHeight:1.8, padding:"6px 10px", cursor:"pointer",
                  background: sortBy===key ? `${C.purpleMid}4d` : "transparent",
                  border:`2px solid ${sortBy===key ? C.purpleMid : C.bgFlat}`,
                  color: sortBy===key ? C.purple : C.textSub }}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Account list */}
        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:C.textSub, fontFamily:PX, fontSize:9 }}>LOADING…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:40, color:C.textSub, fontSize:13, fontWeight:700 }}>
            {profiles.length === 0 ? "No accounts found." : "No accounts match your search."}
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {filtered.map(profile => {
              const mastered = masteredCount(profile.levelProgress);
              const masteredPct = Math.round((mastered / flatLevelCount) * 100);
              const bossKills = Object.keys(profile.bossKills || {}).length;
              const isSelected = selected?.id === profile.id;
              return (
                <div key={profile.id}
                  onClick={() => setSelected(profile)}
                  style={{ background:C.bg, border:`2px solid ${isSelected ? C.purpleMid : C.bgFlat}`,
                    padding:"14px 18px", cursor:"pointer", display:"flex",
                    alignItems:"center", gap:16, flexWrap:"wrap",
                    transition:"border-color 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = C.purpleMid}
                  onMouseLeave={e => e.currentTarget.style.borderColor = isSelected ? C.purpleMid : C.bgFlat}>

                  {/* Avatar letter */}
                  <div style={{ width:40, height:40, background:`${C.purpleMid}33`, border:`2px solid ${C.purpleMid}`,
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
                    fontFamily:PX, fontSize:12, color:C.purple }}>
                    {(profile.name?.[0] ?? "?").toUpperCase()}
                  </div>

                  {/* Name & PIN */}
                  <div style={{ flex:"1 1 120px", minWidth:0 }}>
                    <div style={{ fontWeight:900, fontSize:14, color:C.text, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{profile.name || "Unnamed"}</div>
                    <div style={{ fontSize:11, color:C.textDim, fontWeight:700 }}>PIN: {maskPin(profile.id)}</div>
                  </div>

                  {/* Progress bar */}
                  <div style={{ flex:"2 1 180px", minWidth:140 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, fontWeight:700, color:C.textSub, marginBottom:3 }}>
                      <span>Mastery</span><span>{mastered}/{flatLevelCount}</span>
                    </div>
                    <div style={{ height:8, background:C.bgFlat, overflow:"hidden" }}>
                      <div style={{ width:`${masteredPct}%`, height:"100%", background:C.gold, transition:"width 0.3s" }} />
                    </div>
                  </div>

                  {/* Quick stats */}
                  <div style={{ display:"flex", gap:16, flexShrink:0 }}>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:15, fontWeight:900, color:C.green }}>{(profile.totalQuestions ?? 0).toLocaleString()}</div>
                      <div style={{ fontSize:9, color:C.textDim, fontWeight:700 }}>Questions</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:15, fontWeight:900, color:C.gold }}>{profile.streak ?? 0}d</div>
                      <div style={{ fontSize:9, color:C.textDim, fontWeight:700 }}>Streak</div>
                    </div>
                    <div style={{ textAlign:"center" }}>
                      <div style={{ fontSize:15, fontWeight:900, color:C.purple }}>{profile.badges?.length ?? 0}</div>
                      <div style={{ fontSize:9, color:C.textDim, fontWeight:700 }}>Badges</div>
                    </div>
                    {bossKills > 0 && (
                      <div style={{ textAlign:"center" }}>
                        <div style={{ fontSize:15, fontWeight:900, color:C.red }}>{bossKills}</div>
                        <div style={{ fontSize:9, color:C.textDim, fontWeight:700 }}>Bosses</div>
                      </div>
                    )}
                  </div>

                  {/* Last active */}
                  <div style={{ fontSize:11, fontWeight:700, color:C.textDim, flexShrink:0, textAlign:"right", maxWidth:160 }}>
                    {lastUsedLabel(profile)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selected && (
        <AccountModal
          profile={selected}
          onClose={() => setSelected(null)}
          onDelete={() => handleDelete(selected)}
          onResetProgress={() => handleResetProgress(selected)}
        />
      )}
    </div>
  );
}
